import { STATUS_LOADING } from 'constants/MediaConstants';

import { FETCH_APPOINTMENT_SUCCESS, PUT_SUCCESS as UPDATE_APPOINTMENT_SUCCESS } from 'modules/appointments/actionTypes';
import * as auditActions from 'modules/audits/actionTypes';
import { FETCH_CONVERSATION_ATTACHMENTS_SUCCESS } from 'modules/chat/actionTypes';
import * as actionTypes from 'modules/interventions/actionTypes';
import * as mediaActions from 'modules/medias/actionTypes';

import { getAdvices } from 'services/ChecklistUtils';
import { createEntityAdapter, createReducer } from 'utils/ReduxUtils';

const mediaExist = (state, mediaId) => state[mediaId] !== undefined;

/** @typedef {import('utils/ReduxUtils').EntitySlice<import("modules/medias/types/abstract-media").AbstractMedia>} MediasDataEntity */

/** @type {import('utils/ReduxUtils').EntityAdapter<import("modules/medias/types/abstract-media").AbstractMedia>} */
const mediaAdapter = createEntityAdapter({ selectId: ({ id }) => id });

const updateFromIntervention = (state, { payload: intervention }) =>
  mediaAdapter.updateMany(state, intervention.medias ?? []);

const updateFromAppointment = (state, { payload: appointment }) => {
  const medias = [
    ...(appointment.intervention?.medias ?? []),
    ...(getAdvices(appointment.intervention?.checklists, undefined, (_, __, advice) => advice.medias ?? []).flatMap(
      ({ data, ...rest }) => ({ ...rest, ...data })
    ) ?? []),
    ...(appointment.appointmentTasks?.flatMap(({ task }) => task?.medias ?? []) ?? []),
  ];

  return mediaAdapter.updateMany(state, medias);
};

const updateFromAudit = (state, { payload: audit }) => {
  const medias = audit.sections.flatMap(section =>
    section.questions.flatMap(question =>
      [
        question.media,
        ...(question.medias ?? []),
        ...(question.markers?.flatMap(marker => marker.medias ?? []) ?? []),
      ].filter(Boolean)));

  return mediaAdapter.updateMany(state, medias);
};

const isMediaPatchMatcher = (action, state) =>
  action.type === mediaActions.PATCH_MEDIA && mediaExist(state, action.payload.request.data.id);

const isMediaPatchSuccessMatcher = (action, state) =>
  action.type === mediaActions.PATCH_MEDIA_SUCCESS && mediaExist(state, action.payload.id);

export default createReducer(
  mediaAdapter.getInitialState(),
  {
    // Appointments & interventions
    [FETCH_APPOINTMENT_SUCCESS]: updateFromAppointment,
    [UPDATE_APPOINTMENT_SUCCESS]: updateFromAppointment,
    [actionTypes.FETCH_INTERVENTION_SUCCESS]: updateFromIntervention,
    [actionTypes.UPDATE_INTERVENTION_SUCCESS]: updateFromIntervention,
    // Audits
    [auditActions.PUT_SUCCESS]: updateFromAudit,
    [auditActions.FETCH_AUDIT_SUCCESS]: updateFromAudit,
    // Media from chat attachments
    [FETCH_CONVERSATION_ATTACHMENTS_SUCCESS]: (state, { payload }) => mediaAdapter.updateMany(state, payload.content),
    // Delete medias
    [mediaActions.DELETE_MEDIA_SUCCESS]: (state, { payload: { mediaId } }) => mediaAdapter.removeOne(state, mediaId),
  },
  [
    {
      matcher: isMediaPatchMatcher,
      reducer: (state, { payload }) =>
        mediaAdapter.updateOne(state, { id: payload.request.data.id, status: STATUS_LOADING }),
    },
    {
      matcher: isMediaPatchSuccessMatcher,
      reducer: (state, { payload: media }) => mediaAdapter.updateOne(state, { ...media, status: undefined }),
    },
  ]
);
